import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronLeftBold } from '@sorare/core/src/atoms/icons/ChevronLeftBold';
import { NBALogo } from '@sorare/core/src/atoms/icons/NBALogo';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import type { MoveToElitePackButton_cardPack } from './__generated__/index.graphql';

const Root = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
`;
const RoundedWrapper = styled(Horizontal)`
  width: 54px;
  padding: var(--unit);
  border-radius: 0 var(--triple-unit) var(--triple-unit) 0;
  border-top: 1px solid var(--c-nd-150);
  border-right: 1px solid var(--c-nd-150);
  border-bottom: 1px solid var(--c-nd-150);
  background: var(--c-nd-50);
  justify-content: flex-end;
`;
const StyledVertical = styled(Vertical)`
  align-items: flex-end;
`;
const LogoWrapper = styled.div`
  width: var(--triple-unit);
  height: var(--triple-unit);
  overflow: hidden;
`;
const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const StyledNBALogo = styled(NBALogo)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const IconWrapper = styled(Horizontal).attrs({ center: true })`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  background: var(--c-black);
  border-radius: 50%;
`;

type Props = { onClick: () => void; cardPack: MoveToElitePackButton_cardPack };
export const MoveToElitePackButton = ({ onClick, cardPack }: Props) => {
  const { pictureUrl, displayName } = cardPack.competition || {};
  const { sport } = useSportContext();
  return (
    <Root>
      <ButtonBase onClick={onClick}>
        <StyledVertical>
          <RoundedWrapper>
            <LogoWrapper>
              {pictureUrl ? (
                <LogoImage src={pictureUrl} alt={displayName} />
              ) : (
                sport === Sport.NBA && <StyledNBALogo />
              )}
            </LogoWrapper>
          </RoundedWrapper>

          <Vertical center>
            <BrandXS
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color':
                    'var(--c-gradient-conversionCreditBorder)',
                } as CSSProperties
              }
            >
              <FormattedMessage
                id="BuyPacks.MoveToElitePackButton"
                defaultMessage="Elite"
              />
            </BrandXS>
            <IconWrapper>
              <ChevronLeftBold />
            </IconWrapper>
          </Vertical>
        </StyledVertical>
      </ButtonBase>
    </Root>
  );
};

MoveToElitePackButton.fragments = {
  cardPack: gql`
    fragment MoveToElitePackButton_cardPack on CardPack {
      slug
      competition {
        slug
        pictureUrl
        displayName
      }
    }
  ` as TypedDocumentNode<MoveToElitePackButton_cardPack>,
};
